import React, { useContext, useEffect } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	IItemForGetShortInfoWithStatus,
	UserRoles,
	IGetRestaurantsShort,
	ITotalPrice,
	IGetRestaurantsShortArgs,
} from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { useFetchData } from 'hooks/useFetchData';
import { useRoleAccess } from 'hooks/useRoleAccess';
import MultipleSelect from './components/MultipleSelect';
import style from './RestaurantsMultipleSelect.module.scss';

interface IRestaurantsMultipleSelect {
	setFetchedRestaurants: (value: IItemForGetShortInfoWithStatus[]) => void;
	setRestaurantsForPrice: (value: string[]) => void;
	removePrice: (value: string[]) => void;
	selectedCustomer: Nullable<string>;
	restaurantsForPrice: string[];
	totalPrices: ITotalPrice[];
	externalId: string;
}

const RestaurantsMultipleSelect: React.FC<IRestaurantsMultipleSelect> = ({
	selectedCustomer,
	restaurantsForPrice,
	totalPrices,
	externalId,
	setFetchedRestaurants,
	setRestaurantsForPrice,
	removePrice,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		selectedCustomer ? getRestaurantsShortInfo : null,
		{ customerId: selectedCustomer || '', menuApiVersion: 'v1' },
	);

	useEffect(() => {
		refetch();
	}, [selectedCustomer]);

	useEffect(() => {
		if (response) {
			const accessibleRestaurants = loggedUser.restaurants ? loggedUser.restaurants : [];
			const restaurants = hasAccess ? response.data.restaurants : accessibleRestaurants;
			setFetchedRestaurants(restaurants);
		}
	}, [response, loggedUser, hasAccess]);

	return (
		<div className={style.container}>
			<div className={style.field}>
				<MultipleSelect
					data={response?.data.restaurants || []}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					restaurantsForPrice={restaurantsForPrice}
					selectedCustomer={selectedCustomer}
					totalPrices={totalPrices}
					externalId={externalId}
					setRestaurantsForPrice={setRestaurantsForPrice}
					removePrice={removePrice}
				/>
			</div>
		</div>
	);
};

export default React.memo(RestaurantsMultipleSelect);
